import { Component, input, output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { GivingInfoDialogComponent, GivingInfoDialogData } from '../giving-info-dialog/giving-info-dialog.component';

/**
 * Interface for GivingSpace block data
 *
 * @example
 * ```typescript
 * // Default layout: content on left, image on right
 * const data: GivingSpaceBlockData = {
 *   heading: 'What is GivingSpace?',
 *   description: '<p>Description here...</p>',
 *   buttonLabel: 'Learn More',
 *   imageSrc: 'assets/images/example.png',
 *   imageAlt: 'Example image',
 *   reverse: false
 * };
 *
 * // Reversed layout: image on left, content on right
 * const reversedData: GivingSpaceBlockData = {
 *   heading: 'Our Impact',
 *   description: '<p>Description here...</p>',
 *   buttonLabel: 'Read More',
 *   imageSrc: 'assets/images/impact.png',
 *   imageAlt: 'Impact image',
 *   reverse: true  // This will flip the layout
 * };
 * ```
 */
export interface GivingSpaceBlockData {
  /**
   * The heading text
   */
  heading: string;

  /**
   * The description content (can be HTML or plain text)
   */
  description: string;

  /**
   * The button label
   */
  buttonLabel: string;

  /**
   * The image source URL
   */
  imageSrc: string;

  /**
   * The image alt text
   * @default 'GivingSpace'
   */
  imageAlt?: string;

  /**
   * Whether to reverse the layout
   * - `false` (default): Content on left, image on right
   * - `true`: Image on left, content on right
   *
   * Note: On mobile devices (< 768px), the layout automatically stacks vertically.
   * When reversed on mobile, the image appears above the content.
   *
   * @default false
   */
  reverse?: boolean;

  /**
   * Optional dialog data - if provided, clicking the button will open the dialog with this data
   */
  dialogData?: GivingInfoDialogData;
}

@Component({
  selector: 'app-giving-space-block',
  standalone: false,
  templateUrl: './giving-space-block.component.html',
  styleUrl: './giving-space-block.component.scss',
})
export class GivingSpaceBlockComponent {
  /**
   * The data object containing all configuration for the block
   */
  data = input.required<GivingSpaceBlockData>();

  /**
   * Emits when the button is clicked
   */
  buttonClicked = output<void>();

  constructor(
    private dialog: MatDialog,
    private router: Router
  ) {}

  onButtonClick(): void {
    // If dialog data is provided, handle it
    if (this.data().dialogData) {
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        // On mobile, navigate to the giving-info page with state
        this.router.navigate(['/giving-info'], {
          state: { data: this.data().dialogData }
        });
      } else {
        // On desktop, open the dialog
        this.dialog.open(GivingInfoDialogComponent, {
          data: this.data().dialogData,
          panelClass: 'giving-info-dialog-container',
          maxWidth: '90vw',
          width: '700px',
        });
      }
    }

    // Always emit the event for backward compatibility
    this.buttonClicked.emit();
  }
}
